import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, ChevronLeft, Search, Plus, Sliders } from "lucide-react";
import PrivateLayout from "../components/PrivateLayout";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../services/api";

/**
 * AdminCompetences (V3.19)
 * ────────────────────────
 * Vue Super Admin :
 *  - Liste + recherche + activation/dépréciation des compétences
 *  - Panneau des pondérations matching (déclarée / évaluée / démontrée / validée)
 *  - Liste des providers d'évaluation autorisés (assessment_providers)
 */
export default function AdminCompetences() {
  const { user } = useAuth();
  const isAdmin = ["admin", "super-admin"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [weights, setWeights] = useState(null);
  const [providers, setProviders] = useState([]);
  const [savingW, setSavingW] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDomain, setNewDomain] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, w] = await Promise.all([
        api.get(`/v1/competences?limit=500${q ? `&q=${encodeURIComponent(q)}` : ""}`),
        api.get("/v1/matching/weights"),
      ]);
      setItems(list.data.items || []);
      setWeights(w.data.weights);
      setProviders(w.data.assessment_providers || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const toggleStatus = async (it) => {
    const newStatus = it.status === "active" ? "deprecated" : "active";
    try {
      await api.patch(`/v1/admin/competences/${it.id}`, { status: newStatus });
      toast.success("Statut mis à jour");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const createCompetence = async () => {
    if (!newName.trim()) return toast.error("Nom obligatoire");
    try {
      await api.post("/v1/admin/competences", {
        name: newName.trim(),
        domain: newDomain.trim() || undefined,
      });
      setNewName(""); setNewDomain(""); setShowCreate(false);
      toast.success("Compétence créée");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveWeights = async () => {
    setSavingW(true);
    try {
      await api.patch("/v1/matching/weights", { weights });
      toast.success("Pondérations enregistrées");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingW(false);
    }
  };

  const saveProviders = async (list) => {
    try {
      await api.patch("/v1/matching/weights", { assessment_providers: list });
      setProviders(list);
      toast.success("Providers d'évaluation mis à jour");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!isAdmin) {
    return <PrivateLayout title="Compétences"><div className="afr-empty" data-testid="admin-competences-forbidden">
      <div className="afr-empty__title">Réservé aux administrateurs</div></div></PrivateLayout>;
  }

  return (
    <PrivateLayout
      title="🎓 Référentiel Compétences"
      breadcrumbs={[{ to: "/admin", label: "Admin" }, { label: "Compétences" }]}
      actions={
        <div style={{ display: "flex", gap: 6 }}>
          <button className="afr-btn afr-btn--outline afr-btn--sm" onClick={load}
                  data-testid="admin-competences-refresh"><RefreshCw size={12} /> Actualiser</button>
          <button className="afr-btn afr-btn--primary afr-btn--sm" onClick={() => setShowCreate((s) => !s)}
                  data-testid="admin-competences-toggle-create"><Plus size={12} /> Nouvelle</button>
          <Link to="/admin/command" className="afr-btn afr-btn--outline afr-btn--sm"><ChevronLeft size={14} /> Admin</Link>
        </div>
      }
    >
      <div data-testid="admin-competences-page">
        {/* Weights panel */}
        {weights && (
          <section className="afr-card" style={{ padding: 14, marginBottom: 12 }}
                   data-testid="admin-weights-panel">
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <Sliders size={16} />
              <strong>Pondération du matching (par preuve)</strong>
              <span style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                Valeurs initiales V1. Ajustables à tout moment.
              </span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
              {["declared", "evaluated", "demonstrated", "validated"].map((k) => (
                <div key={k}>
                  <label style={{ fontSize: "0.78rem", color: "#475569" }}>
                    {({ declared: "Déclarée", evaluated: "Évaluée", demonstrated: "Démontrée", validated: "Validée" })[k]}
                  </label>
                  <input
                    type="number" step="0.1" min="0"
                    value={weights[k]}
                    onChange={(e) => setWeights({ ...weights, [k]: Number(e.target.value) })}
                    data-testid={`admin-weight-${k}`}
                    style={{ width: "100%", padding: "6px 8px", border: "1px solid #E2E8F0", borderRadius: 4 }}
                  />
                </div>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
              <button className="afr-btn afr-btn--primary afr-btn--sm" onClick={saveWeights} disabled={savingW}
                      data-testid="admin-weights-save">
                {savingW ? "…" : "Enregistrer"}
              </button>
            </div>
            <div style={{ marginTop: 12, padding: 10, background: "#F1F5F9", borderRadius: 6, fontSize: "0.82rem" }}>
              <strong>Providers d'évaluation autorisés :</strong>{" "}
              {providers.length === 0
                ? <em>aucun (aucune compétence ne peut être marquée « ÉVALUÉE » tant qu'un provider n'est pas activé)</em>
                : providers.map((p) => (
                  <span key={p} data-testid={`admin-provider-${p}`}
                        style={{ padding: "2px 8px", background: "#DBEAFE", color: "#1E40AF", borderRadius: 999, marginRight: 4 }}>{p}</span>
                ))}
              <div style={{ marginTop: 6 }}>
                <input placeholder="ajouter un provider (ex: africtal_stub)"
                       data-testid="admin-provider-input"
                       style={{ padding: "4px 8px", border: "1px solid #E2E8F0", borderRadius: 4, marginRight: 6 }}
                       onKeyDown={(e) => {
                         if (e.key === "Enter" && e.target.value.trim()) {
                           saveProviders([...providers, e.target.value.trim()]);
                           e.target.value = "";
                         }
                       }} />
              </div>
            </div>
          </section>
        )}

        {/* Create */}
        {showCreate && (
          <section className="afr-card" style={{ padding: 12, marginBottom: 12, background: "#F8FAFC" }}
                   data-testid="admin-competences-create">
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr auto", gap: 8 }}>
              <input value={newName} onChange={(e) => setNewName(e.target.value)}
                     placeholder="Nom (ex: Prompt Engineering)"
                     data-testid="admin-competences-name" style={inp} />
              <input value={newDomain} onChange={(e) => setNewDomain(e.target.value)}
                     placeholder="Domaine (ex: AI)"
                     data-testid="admin-competences-domain" style={inp} />
              <button className="afr-btn afr-btn--primary afr-btn--sm" onClick={createCompetence}
                      data-testid="admin-competences-create-submit">Créer</button>
            </div>
          </section>
        )}

        {/* Search + list */}
        <section className="afr-card" style={{ padding: 12 }} data-testid="admin-competences-list-card">
          <div style={{ position: "relative", marginBottom: 12, maxWidth: 400 }}>
            <Search size={14} style={{ position: "absolute", top: 9, left: 8, color: "#94a3b8" }} />
            <input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Rechercher une compétence…"
                   data-testid="admin-competences-search"
                   style={{ width: "100%", padding: "6px 8px 6px 28px", border: "1px solid #E2E8F0", borderRadius: 6 }} />
          </div>
          {loading ? (
            <div>Chargement…</div>
          ) : items.length === 0 ? (
            <div className="afr-empty" data-testid="admin-competences-empty">
              <div className="afr-empty__title">Aucune compétence trouvée</div>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" }}>
                <thead>
                  <tr style={{ background: "#F1F5F9" }}>
                    <th style={th}>Nom</th>
                    <th style={th}>Slug</th>
                    <th style={th}>Domaine</th>
                    <th style={th}>Source</th>
                    <th style={th}>Statut</th>
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id} data-testid={`admin-competence-row-${it.slug}`}>
                      <td style={td}>{it.name}</td>
                      <td style={td}><code>{it.slug}</code></td>
                      <td style={td}>{it.domain || "—"}</td>
                      <td style={td}>{it.source || "—"}</td>
                      <td style={td}>
                        <span style={{
                          padding: "2px 8px", borderRadius: 999, fontSize: "0.72rem", fontWeight: 700,
                          background: it.status === "active" ? "#DCFCE7" : "#FEE2E2",
                          color: it.status === "active" ? "#065F46" : "#991B1B",
                        }}>
                          {it.status}
                        </span>
                      </td>
                      <td style={td}>
                        <button
                          className="afr-btn afr-btn--outline afr-btn--sm"
                          onClick={() => toggleStatus(it)}
                          data-testid={`admin-competence-toggle-${it.slug}`}
                        >
                          {it.status === "active" ? "Déprécier" : "Réactiver"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </PrivateLayout>
  );
}

const th = { textAlign: "left", padding: "8px 10px", fontWeight: 600, fontSize: "0.78rem" };
const td = { padding: "8px 10px", borderBottom: "1px solid #E2E8F0" };
const inp = { padding: "8px 10px", border: "1px solid #E2E8F0", borderRadius: 6, fontSize: "0.9rem" };
